import { App } from 'antd';
import type { NotificationArgsProps } from 'antd';
import React from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { css } from 'config/emotionCss';

import * as styles from './Notification.module.less';
import NotificationExpand from './NotificationExpand';

/** No `Notification` token governs the notice padding or the close button's position. */
const noDescriptionStyle = css`
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  display: flex;

  > .ant-notification-notice-close {
    top: auto;
  }
`;

const getGeneralConfig = (hasDescription: React.ReactNode) => ({
  className: hasDescription ? undefined : noDescriptionStyle,
});

const getErrorConfig = (intl: IntlShape, description: React.ReactNode) => ({
  description: (
    <React.Fragment>
      {description ??
        intl.formatMessage({
          id: 'notification.error.try_again',
          defaultMessage: 'Please try again. If the problem persists, contact the support team.',
        })}
      <NotificationExpand
        buttonTitle={intl.formatMessage({
          id: 'notification.error.contact_expand',
          defaultMessage: 'Support contact information',
        })}
        expandContent={
          <div className={styles['fb-notification-collapse-content']}>
            {intl.formatMessage({ id: 'about.openingTime', defaultMessage: 'Mo-Fr from 9am to 5pm.' })} <br />
            support@fleetback.com <br />
            +32 2 80 80 828
          </div>
        }
      />
    </React.Fragment>
  ),
});

const useNotification = () => {
  const intl = useIntl();
  const { notification } = App.useApp();

  return {
    success: (config: NotificationArgsProps) =>
      notification.success({ ...config, ...getGeneralConfig(config?.description) }),
    'simple-error': (config: NotificationArgsProps) =>
      notification.error({ ...config, ...getGeneralConfig(config?.description) }),
    error: (config: NotificationArgsProps) =>
      notification.error({ ...config, ...getGeneralConfig(true), ...getErrorConfig(intl, config.description) }),
    info: (config: NotificationArgsProps) =>
      notification.info({ ...config, ...getGeneralConfig(config?.description) }),
    warning: (config: NotificationArgsProps) =>
      notification.warning({ ...config, ...getGeneralConfig(config?.description) }),
    open: (config: NotificationArgsProps) => notification.open({ ...config }),
    destroy: (key?: React.Key) => notification.destroy(key),
  };
};

export default useNotification;
